import { useState } from 'react'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { ChipRow, InputRow, MoneyRow, NavRow, SegRow } from '../components/Controls'
import { IntakeCard, todayCard, CARD_CSS, type CardData } from '../components/IntakeCard'
import { useStore } from '../state/store'
import { intakeNoLabel } from '../lib/api'
import { BrandSheet } from '../components/BrandSheet'
import { CategorySheet } from '../components/CategorySheet'
import { categoryLabel } from '../lib/categories'
import { LABEL, fmt, feePct, FEE_OPTIONS, consignorName, pieceName } from '../lib/types'
import type { IntakeDraft } from '../lib/types'
import { descriptionOf, kindOf, measureDefs, measureShort, measuresOf, renderHtml, sections, showGraphics, showTag, type ListingDraft } from '../../supabase/functions/_shared/listing-description.ts'
import { COLORS, ERAS, GRAPHICS, materialsFor } from '../lib/listingOptions'
import { consignedDeal, consignedSentence } from '../lib/intakeCard'

export function cardFromDraft(d: IntakeDraft, intakeNo: number, consName: string, id = ''): CardData {
  const bought = d.type === 'bought'
  const fee = feePct(d)
  if (d.pendingId) return {
    how: 'traded', date: todayCard(), id, from: d.from, deal: d.cost ? `${fmt(d.cost)} trade credit` : '', whose: d.whose,
    sentence: d.note || `Trade-in credited against trade #${d.tradeNo ?? ''}`, what: pieceName(d.brand, d.item, d.size), cond: d.cond, intakeNo, tradeNo: d.tradeNo, authenticated: d.authenticated, measurements: d.measurements,
  }
  if (bought && d.sourced) return {
    how: 'bought', date: todayCard(), id, from: sourcedFromLine(d.source), deal: d.cost ? `${fmt(d.cost)}, sourced by Shane, no payout` : '', whose: d.whose,
    sentence: d.note, what: pieceName(d.brand, d.item, d.size), cond: d.cond, intakeNo, authenticated: d.authenticated, measurements: d.measurements,
  }
  return {
    how: bought ? 'bought' : 'consigned', date: todayCard(), id,
    from: bought ? d.from : consName,
    deal: bought ? (d.cost ? `${fmt(d.cost)}, ${LABEL.payout[d.payout]}` : '') : (fee ? consignedDeal(fee, Number(d.min) || null) : ''),
    whose: bought ? d.whose : 'consignor',
    sentence: d.note || (bought ? '' : (fee ? consignedSentence(fee, Number(d.min) || null) : '')),
    what: pieceName(d.brand, d.item, d.size), cond: d.cond, intakeNo, authenticated: d.authenticated, measurements: d.measurements,
  }
}

/** Who a sourced-by-Shane piece came from, as the card and the review show it. */
export const sourcedFromLine = (source: string) => `Shane Sears${source.trim() ? `, from ${source.trim()}` : ''}`

export function draftReady(d: IntakeDraft) {
  const piece = d.brand.trim() && d.item.trim() && d.category
  if (!piece || !d.authenticated) return false
  if (d.pendingId) return Number(d.cost) > 0
  if (d.type === 'bought' && d.sourced) return Number(d.cost) > 0
  if (d.type === 'bought') return !!d.customerId && Number(d.cost) > 0
  return !!d.consignorId && Number(d.min || 0) >= 0 && feePct(d) > 0 && feePct(d) < 100   // minimum optional (shipped-in consignments)
}

const SITE_COND: Record<string, string> = { nwt: 'NWT', excellent: 'Excellent', good: 'Very Good', fair: 'Good' }

export function Intake() {
  const nav = useNav()
  const { draft: d, setDraft, intakeNo, consignors, customers } = useStore()
  const [brandSheet, setBrandSheet] = useState(false)
  const [catSheet, setCatSheet] = useState(false)
  const bought = d.type === 'bought'
  const sourced = bought && d.sourced
  const cons = consignors.find((c) => c.id === d.consignorId)
  const cust = customers.find((c) => c.id === d.customerId)
  const card = cardFromDraft(d, intakeNo, consignorName(cons))
  const dealShort = bought ? (d.cost ? fmt(d.cost) : '') : (feePct(d) ? `${feePct(d)}% fee${Number(d.min) > 0 ? ` · min ${fmt(d.min)}` : ''}` : '')
  const pending = !!d.pendingId
  // Listing details (Shane's description format): tap chips and numbers, and the website description builds itself.
  const L = d.listing, kind = kindOf(d.category, d.item), defs = measureDefs(kind, d.item, d.category)
  const setListing = (patch: Partial<ListingDraft>) => {
    const next = { ...L, ...patch }
    setDraft({ listing: next, ...(defs.length ? { measurements: measureShort(measuresOf(next, kind, d.item, d.category)) } : {}) })
  }
  const descText = descriptionOf(L, d.brand, d.item)
  const secs = sections(L, d.brand, d.item, kind, d.category)
  const review = () => {
    if (pending) return nav('/intake/review')
    if (!bought && !cons) return nav('/consignors?pick=1')
    if (bought && !sourced && !cust) return nav('/customers?pick=1')
    nav('/intake/review')
  }
  return (
    <Page
      bar={<TopBar back="Home" backTo="/" title={pending ? `Finish trade-in from trade #${d.tradeNo ?? ''}` : 'New intake'} caption={`Intake #${intakeNoLabel(intakeNo)}`} />}
      aside={
        <>
          <div className="aside__body">
            <div className="icard-head"><b>Intake card</b><span>5×7, prints on confirm</span></div>
            <style>{CARD_CSS}</style>
            <div className="ic-preview"><IntakeCard c={card} /></div>
            <div className="icard-head" style={{ marginTop: 14 }}><b>Website text</b><span>description, condition, measurements</span></div>
            <div className="hint" style={{ padding: '0 20px 6px' }}>Condition scale on the site: <b>{SITE_COND[d.cond]}</b></div>
            {secs.length ? <div className="desc-prev" dangerouslySetInnerHTML={{ __html: renderHtml(secs) }} /> : <div className="desc-prev desc-prev--empty">Fill in the details and it appears here.</div>}
            <div className="hint" style={{ padding: '10px 20px' }}>Measurements print if you enter them, otherwise the line stays blank to write on. The website text is the description, condition notes and measurements; color, material, tag, era and graphics are saved with the piece but not shown. The tick row stays blank for the processing line. The barcode tag prints later, once the listing is live.</div>
          </div>
          <div className="aside__foot">
            <button type="button" className="cta" onClick={review} disabled={!draftReady(d) && !(!pending && d.brand && d.item && d.category && ((!bought && !cons) || (bought && !sourced && !cust)))}><span>Review intake</span><span>{dealShort}</span></button>
          </div>
        </>
      }
    >
      {pending && <div className="banner">This piece came in on trade #{d.tradeNo ?? ''} with a credit of {fmt(d.cost)}, which is its cost. Finish the size, measurements and authentication; Shane prices it when he lists it.</div>}
      {!pending && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <button type="button" className="seg__btn" onClick={() => nav('/intake/pending')}>Pending from trades ›</button>
          <button type="button" className="seg__btn" onClick={() => nav('/intake/recent')}>Recent intakes ›</button>
        </div>
      )}
      <div className="section-title">Deal</div>
      <div className="card">
        {pending ? <div className="row"><span className="row__label">Coming in as</span><span className="row__value">Traded · from {d.from} · {LABEL.whose[d.whose]}'s piece · cost {fmt(d.cost)}</span></div>
        : <SegRow label="Coming in as" value={sourced ? 'sourced' : d.type} options={[{ v: 'bought', label: 'Bought' }, { v: 'sourced', label: 'Sourced by Shane' }, { v: 'consigned', label: 'Consigned' }]} onChange={(v) => setDraft(v === 'sourced' ? { type: 'bought', sourced: true } : { type: v as 'bought' | 'consigned', sourced: false })} />}
        {pending ? null : sourced ? (
          <InputRow id="source" label="Where from" value={d.source} onChange={(source) => setDraft({ source })} placeholder="Chrome Hearts store, eBay, who he paid (optional)" />
        ) : bought ? (
          <NavRow label="Bought from" value={cust ? d.from : 'Pick or add'} set={!!cust} onClick={() => nav('/customers?pick=1')} />
        ) : (
          <NavRow label="Consignor" value={cons ? consignorName(cons) : 'Pick or add'} set={!!cons} onClick={() => nav('/consignors?pick=1')} />
        )}
        {pending ? (
          <SegRow label="Whose piece" value={d.whose} options={[{ v: 'shane', label: 'Shane' }, { v: 'masih', label: 'Masih' }, { v: 'split', label: 'Split' }]} onChange={(whose) => setDraft({ whose })} />
        ) : bought ? (
          <>
            <MoneyRow id="cost" label="Cost paid" value={d.cost} onChange={(cost) => setDraft({ cost })} placeholder="$0" />
            <SegRow label="Whose piece" value={d.whose} options={[{ v: 'shane', label: 'Shane' }, { v: 'masih', label: 'Masih' }, { v: 'split', label: 'Split' }]} onChange={(whose) => setDraft({ whose })} />
          </>
        ) : (
          <>
            <SegRow label="Starchive fee" value={d.fee} options={FEE_OPTIONS} onChange={(fee) => setDraft({ fee })} />
            {d.fee === 'other' && <MoneyRow id="feeOther" label="Custom fee" value={d.feeOther} onChange={(feeOther) => setDraft({ feeOther })} placeholder="0%" prefix="" suffix="%" decimal max={4} hint="Starchive's share of the sale" />}
            <MoneyRow id="min" label="Consignor minimum" value={d.min} onChange={(min) => setDraft({ min })} placeholder="Optional" hint="Lowest they'll accept. Leave blank for a shipped-in piece with no floor; staff can't sell below a floor without an owner" />
          </>
        )}
        {sourced && <div className="row"><span className="row__label">Payout</span><span className="row__value">None. Shane already paid for it; the cost still counts</span></div>}
        {bought && !sourced && !pending && <SegRow label="Paid out by" value={d.payout} options={[{ v: 'cash', label: 'Cash' }, { v: 'zelle', label: 'Zelle' }, { v: 'wire', label: 'Wire' }, { v: 'crypto', label: 'Crypto' }]} onChange={(payout) => setDraft({ payout })} />}
      </div>

      <div className="section-title">Piece</div>
      <div className="card">
        <NavRow label="Brand" value={d.brand || 'Pick or add'} set={!!d.brand} onClick={() => setBrandSheet(true)} />
        <NavRow label="Category" value={d.category ? categoryLabel(d.category) : 'Pick'} set={!!d.category} onClick={() => setCatSheet(true)} />
        <InputRow id="item" label="Item" value={d.item} onChange={(item) => setDraft({ item: item.toUpperCase() })} placeholder="WHAT IT IS" caps />
        <InputRow id="size" label="Size" value={d.size} onChange={(size) => setDraft({ size })} placeholder="M, 43, 32, OS…" />
        <SegRow label="Authenticated by Christian" value={d.authenticated ? 'yes' : 'no'} options={[{ v: 'yes', label: 'Yes' }, { v: 'no', label: 'Not yet' }]} onChange={(v) => setDraft({ authenticated: v === 'yes' })} />
      </div>

      <div className="section-title">Listing details <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--caption)' }}>color to graphics are saved, not shown on the site</span></div>
      <div className="card">
        <div className="descbox">
          <div className="chiprow__head"><span className="row__label">Description</span><span className="chiprow__hint">one short sentence</span></div>
          <textarea id="desc" value={descText} placeholder="Era, brand, what it is, the graphic" onChange={(e) => setListing({ description: e.target.value, descriptionEdited: true })} autoCapitalize="sentences" />
          <div className="descbox__foot"><span>{L.descriptionEdited ? 'Typed by hand' : 'Writes itself from the era, item and graphics. Tap to change it.'}</span>{L.descriptionEdited && <button type="button" onClick={() => setListing({ description: '', descriptionEdited: false })}>Write it for me again</button>}</div>
        </div>
        <ChipRow label="Color" options={COLORS} value={L.color} onChange={(color) => setListing({ color })} hint="pick all that show" />
        <ChipRow label="Material" options={materialsFor(kind)} value={L.material} onChange={(material) => setListing({ material })} />
        {showTag(kind) && <InputRow id="tag" label="Tag" value={L.tag} onChange={(tag) => setListing({ tag })} placeholder="Type what the tag says, e.g. Tultex" />}
        <ChipRow label="Era" options={ERAS} value={L.era ? [L.era] : []} multi={false} onChange={(v) => setListing({ era: v[0] ?? '' })} />
        {showGraphics(kind) && <ChipRow label="Graphics" options={GRAPHICS} value={L.graphics} onChange={(graphics) => setListing({ graphics })} />}
        <SegRow label="Condition" value={d.cond} options={[{ v: 'nwt', label: 'NWT' }, { v: 'excellent', label: 'Excellent' }, { v: 'good', label: 'Good' }, { v: 'fair', label: 'Fair' }]} onChange={(cond) => setDraft({ cond })} />
        <div className="descbox"><div className="chiprow__head"><span className="row__label">Condition notes</span><span className="chiprow__hint">type it, leave empty if clean</span></div>
          <textarea id="notes" value={L.notes} placeholder="e.g. Paint splatter all over" onChange={(e) => setListing({ notes: e.target.value })} autoCapitalize="sentences" /></div>
      </div>

      <div className="section-title">Measurements <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--caption)' }}>{defs.length ? 'inches, flat' : ''}</span></div>
      <div className="card">
        {defs.length ? (
          <div className="meas-grid">
            {defs.map((m) => <MoneyRow key={m.key} id={`m-${m.key}`} label={m.label} value={L.measures[m.key] ?? ''} onChange={(v) => setListing({ measures: { ...L.measures, [m.key]: v } })} placeholder="–" prefix="" suffix={m.unit} decimal max={5} />)}
          </div>
        ) : (
          <InputRow id="meas" label="Measurements" value={d.measurements} onChange={(measurements) => setDraft({ measurements })} placeholder="Optional, e.g. P2P 22 · L 28" />
        )}
      </div>

      <div className="section-title">Deal note</div>
      <div className="card">
        <InputRow id="note" label="Deal note" value={d.note} onChange={(note) => setDraft({ note })} placeholder="The deal in a sentence (optional)" />
      </div>
      {catSheet && <CategorySheet value={d.category} onPick={(category, categoryName) => { setDraft({ category, categoryName }); setCatSheet(false) }} onClose={() => setCatSheet(false)} />}
      {brandSheet && <BrandSheet value={d.brand} onPick={(brand) => { setDraft({ brand }); setBrandSheet(false) }} onClose={() => setBrandSheet(false)} />}
    </Page>
  )
}
